import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: string;
  hideCloseButton?: boolean;
};

// Matches the .animate-dialog-out / .animate-scrim-out duration in index.css — the exit
// animation must finish before the modal actually unmounts (same pattern as PortalLayout's
// mobile sidebar), otherwise `open` flipping to false just cuts the dialog away instantly.
const EXIT_ANIMATION_MS = 220;

export function Modal({ open, onClose, title, children, maxWidth = 'max-w-md', hideCloseButton = false }: Props) {
  const [shouldRender, setShouldRender] = useState(open);
  const [closing, setClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setClosing(false);
    } else if (shouldRender) {
      setClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setClosing(false);
      }, EXIT_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shouldRender]);

  // Move focus into the dialog on open, and restore it to whatever triggered the modal on
  // close — otherwise keyboard/screen-reader users land back at the top of the page instead of
  // where they were.
  useEffect(() => {
    if (open) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      const raf = requestAnimationFrame(() => {
        const dialog = dialogRef.current;
        const focusable = dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (focusable ?? dialog)?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }
    previouslyFocused.current?.focus();
  }, [open]);

  // Esc-to-close plus a Tab focus trap — while open, Tab/Shift+Tab cycle only through the
  // dialog's own focusable elements instead of escaping to the page behind the scrim.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!shouldRender) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Scrim */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm ${closing ? 'animate-scrim-out' : 'animate-scrim-in'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog — capped to the viewport with a scrollable body so long forms (e.g. a modal
          with several field groups) never push the submit button off-screen unreachable. */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`relative w-full ${maxWidth} max-h-[85vh] flex flex-col rounded-2xl bg-bg shadow-2xl outline-none ${closing ? 'animate-dialog-out' : 'animate-dialog-in'}`}
      >
        {/* Title bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line flex-shrink-0">
          {title ? (
            <h2 className="text-sm font-black text-text">{title}</h2>
          ) : (
            <span />
          )}
          {!hideCloseButton && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              className="rounded-xl"
              aria-label="Close"
            >
              <X size={14} />
            </Button>
          )}
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
